import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// import { UserHeader } from "../components/UserHeader";
import styles from "./css/ProfilePage.module.css";
import { getSession, jwtDecode } from "../auth/auth.utils";
import { useAppSelector, useAppDispatch } from "../redux/store";
import { AnalystService } from "../services/analyst.service";
import { setAuth } from "../redux/auth/auth.slice";

// קבוע לשמירת הפרופיל בלוקל סטורג'
const STORAGE_KEY_PROFILE = "analyst_profile_data";

// מודל נתונים עבור פרטי האנליסט
interface ProfileData {
  code: number;        // קוד אנליסט
  id: string;          // תעודת זהות
  fname: string;       // שם פרטי
  lname: string;       // שם משפחה
  email: string;       // אימייל
  role: string;        // תפקיד
}

// פונקציית עזר לשמירת פרופיל בלוקל סטורג'
const saveProfileToLocalStorage = (profile: ProfileData) => {
  localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile));
};

// פונקציית עזר לטעינת פרופיל מלוקל סטורג'
const loadProfileFromLocalStorage = (): ProfileData | null => {
  const savedProfile = localStorage.getItem(STORAGE_KEY_PROFILE);
  if (savedProfile) {
    try {
      return JSON.parse(savedProfile);
    } catch (e) {
      console.error("שגיאה בפענוח נתוני פרופיל מהלוקל סטורג':", e);
    }
  }
  return null;
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    code: 0,
    id: "",
    fname: "",
    lname: "",
    email: "",
    role: ""
  });
  const [originalData, setOriginalData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // קבלת נתוני המשתמש מ-Redux
  const user = useAppSelector(state => state.auth.user);

  useEffect(() => {
    console.log("[DEBUG] ProfilePage useEffect נטען");
    console.log("[DEBUG] האם יש טוקן:", getSession() ? "כן" : "לא");
    
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // בהתחלה, נבדוק אם יש נתונים בלוקל סטורג'
        const savedProfile = loadProfileFromLocalStorage();
        if (savedProfile) {
          console.log("נטען פרופיל מהלוקל סטורג':", savedProfile);
          setProfileData(savedProfile);
          setOriginalData(savedProfile);
          setIsLoading(false);
          return; // אם יש נתונים בלוקל סטורג', לא צריך להמשיך
        }
    
        // אם אין נתונים בלוקל סטורג', ננסה לטעון מהשרת או מ-Redux
        console.log("נתוני משתמש מ-Redux:", user);
        
        // אם יש לנו את המשתמש ב-Redux, נשתמש בו
        if (user) {
          // ננסה לקבל נתונים מלאים מהשרת באמצעות שירות האנליסט אם יש קוד אנליסט
          if (user.code) {
            try {
              console.log("מנסה לטעון נתוני אנליסט מהשרת לפי קוד:", user.code);
              const analystData = await AnalystService.getById(user.code);
              console.log("נתוני אנליסט מהשרת:", analystData);
              
              // המרת נתוני האנליסט לפורמט שלנו
              const fullProfileData: ProfileData = {
                code: analystData.code,
                id: analystData.idNumber,
                fname: analystData.firstName,
                lname: analystData.lastName,
                email: analystData.email,
                role: "analyst"
              };
              
              setProfileData(fullProfileData);
              setOriginalData(fullProfileData);
              saveProfileToLocalStorage(fullProfileData);
              setIsLoading(false);
              return;
            } catch (error) {
              console.error("שגיאה בקבלת נתוני אנליסט מהשרת:", error);
              // נמשיך לקוד הבא אם נכשל
            }
          }
          
          // אם לא הצלחנו להשיג נתונים מהשרת, נשתמש בנתונים מ-Redux
          const reduxProfileData: ProfileData = {
            code: user.code || 0,
            id: user.id || "",
            fname: user.name?.split(' ')[0] || "",
            lname: user.name?.split(' ')[1] || "",
            email: user.email || "",
            role: user.role?.toLowerCase() || "analyst"
          };
          
          console.log("משתמש בנתונים מ-Redux:", reduxProfileData);
          setProfileData(reduxProfileData);
          setOriginalData(reduxProfileData);
          setIsLoading(false);
          return;
        }
        // אם אין נתונים ב-Redux, ננסה לקחת מהטוקן
        const token = getSession();
        if (token) {
          const decoded = jwtDecode(token);
          
          // הדפסת הטוקן המפוענח לדיבוג - מידע מפורט יותר
          console.log("נתוני טוקן מפוענח (מלא):", decoded);
          console.log("מפתחות בטוקן:", Object.keys(decoded));
          
          // הדפסה של כל שדה בנפרד כדי להבין מה הערכים שקיימים
          console.log("fname מהטוקן:", decoded?.["fname"]);
          console.log("firstName מהטוקן:", decoded?.["firstName"]);
          console.log("name מהטוקן:", decoded?.["name"]);
          console.log("lname מהטוקן:", decoded?.["lname"]);
          console.log("lastName מהטוקן:", decoded?.["lastName"]);
          
          // קוד האנליסט נמצא בטוקן
          const analystCode = decoded?.["code"] || decoded?.["analystCode"] || decoded?.["userId"] || 0;
          
          // יצירת ערכי ברירת מחדל מהטוקן (למקרה שהבקשה לשרת תיכשל)
          // כאן אנחנו מנסים למצוא את כל הערכים האפשריים בטוקן
          const defaultUserData: ProfileData = {
            code: analystCode,
            id: decoded?.["id"] || decoded?.["userId"] || decoded?.["identityNumber"] || decoded?.["ID"] || "",
            fname: decoded?.["fname"] || decoded?.["firstName"] || decoded?.["first_name"] || decoded?.["Fname"] || 
                   (decoded?.["name"] ? decoded?.["name"].split(" ")[0] : "") || "",
            lname: decoded?.["lname"] || decoded?.["lastName"] || decoded?.["last_name"] || decoded?.["Lname"] || 
                   (decoded?.["name"] && decoded?.["name"].split(" ").length > 1 ? decoded?.["name"].split(" ")[1] : "") || "",
            email: decoded?.["email"] || decoded?.["mail"] || decoded?.["Email"] || "",
            role: decoded?.["role"] || decoded?.["Role"] || "analyst"
          };
          
          // הדפסה של הנתונים שנמצאו מהטוקן לדיבוג
          console.log("נתונים שנמצאו בטוקן:", defaultUserData);
          
          // בדיקה אם יש נתונים מספיקים בטוקן, אם לא - נשתמש בנתוני דמו
          if (!defaultUserData.fname || !defaultUserData.lname) {
            // יצירת נתונים קבועים לתצוגה במקום טעינה מהשרת לצורך הדגמה
            const demoData: ProfileData = {
              code: 14, // קוד אנליסט
              id: "061265815", // תעודת זהות
              fname: "נעמי", // שם פרטי
              lname: "כהן", // שם משפחה
              email: "naomi@example.com", // אימייל
              role: "analyst" // תפקיד
            };
            
            console.log("אין מספיק נתונים בטוקן, משתמש בנתוני דמו:", demoData);
            setProfileData(demoData);
            setOriginalData(demoData);
          } else {
            // שימוש בנתונים האמיתיים מהטוקן
            console.log("משתמש בנתונים האמיתיים מהטוקן:", defaultUserData);
            setProfileData(defaultUserData);
            setOriginalData(defaultUserData);
          }
          
          try {
            // כאן יכולה להיות קריאה אמיתית לשרת לקבלת נתונים
            console.log("משתמש בנתונים האמיתיים מהטוקן:", defaultUserData);
            setProfileData(defaultUserData);
            setOriginalData(defaultUserData);
          } catch (error) {
            console.error("שגיאה בבקשת נתוני אנליסט:", error);
          }
        }
      } catch (error) {
        console.error("שגיאה כללית בטעינת נתוני משתמש:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(`שדה '${name}' עודכן לערך: '${value}'`);
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // הצגת מצב טעינה
      setIsLoading(true);
      
      console.log("מנסה לעדכן פרטי אנליסט:", profileData);
      
      try {
        // נכין את הנתונים לעדכון - נשתמש בדיוק באותה צורה כמו בדף המנהל
        const analystData = {
          code: profileData.code,
          firstName: profileData.fname.trim(),
          lastName: profileData.lname.trim(),
          idNumber: profileData.id,
          email: profileData.email.trim()
        };
        
        console.log("[DEBUG] נתוני אנליסט לפני עדכון:", analystData);
        console.log("[DEBUG] קוד אנליסט:", profileData.code);
        
        try {
          // עדכון באמצעות שירות האנליסט
          await AnalystService.update(profileData.code, analystData);
          console.log("[DEBUG] פרופיל עודכן בהצלחה בשרת");
        } catch (apiError) {
          console.error("[DEBUG] שגיאה בקריאה לשרת:", apiError);
          throw apiError;
        }
        
        // עדכון הפרופיל המקומי
        const updatedProfile = {
          ...profileData,
          fname: analystData.firstName,
          lname: analystData.lastName,
          email: analystData.email
        };
        
        // שמירה בלוקל סטורג' כדי שהנתונים יישמרו בין רענונים
        saveProfileToLocalStorage(updatedProfile);
        
        // עדכון המצב
        setOriginalData(updatedProfile);
        setProfileData(updatedProfile);
        setIsEditing(false);
        
        // עדכון ה-Redux store עם הנתונים החדשים כדי שהם יופיעו בכותרת
        if (user) {
          // יוצרים אובייקט משתמש מעודכן עם הנתונים החדשים
          const updatedUser = {
            ...user,
            name: `${updatedProfile.fname} ${updatedProfile.lname}`,
            email: updatedProfile.email
          };
          
          // מעדכנים את ה-Redux store
          dispatch(setAuth(updatedUser));
          console.log("[DEBUG] עדכון המשתמש ב-Redux store:", updatedUser);
        }
        
        // הצגת חיווי הצלחה
        setUpdateSuccess(true);
        
        // גורם לרינדור מחדש של הדף אחרי עיכוב קצר
        setIsLoading(true);
        setTimeout(() => {
          setIsLoading(false);
          // מסיר את חיווי ההצלחה אחרי 3 שניות
          setTimeout(() => {
            setUpdateSuccess(false);
          }, 3000);
          console.log("[DEBUG] עמוד רונדר מחדש לאחר עדכון מוצלח");
        }, 500);
        
      } catch (error) {
        console.error("שגיאה בעדכון פרופיל:", error);
        alert("אירעה שגיאה בעדכון הפרופיל. אנא נסה שנית מאוחר יותר.");
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("שגיאה כללית בעדכון פרופיל:", error);
      alert("אירעה שגיאה בעדכון הפרופיל");
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // חזרה למצב המקורי
    if (originalData) {
      setProfileData(originalData);
    }
    setIsEditing(false);
  };

  return (
    <div style={{
      backgroundImage: 'url("/Images/view1.png")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      minHeight: '100vh',
      margin: 0,
      padding: 0
    }}>
      {/* <UserHeader /> */}
      <div className={styles.pageContainer}>
        <div className={styles.card}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loader}></div>
              <p>טוען נתונים...</p>
            </div>
          ) : (
            <>
              {updateSuccess && (
                <div className={styles.successMessage}>
                  <span className={styles.successIcon}>✅</span>
                  הפרופיל עודכן בהצלחה!
                </div>
              )}
              <div className={styles.titleContainer}>
                <h1 className={styles.title}>
                  הפרופיל שלי
                  <span className={styles.titleUnderline}></span>
                </h1>
                {!isEditing && (
                  <button 
                    className={styles.editIconButton} 
                    onClick={() => setIsEditing(true)}
                    title="עריכת פרטים"
                  >
                    ✏️
                  </button>
                )}
              </div>
          
              <form onSubmit={handleSubmit} className={styles.profileForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="fname">שם פרטי:</label>
                <input
                  type="text"
                  id="fname"
                  name="fname"
                  value={profileData.fname}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={styles.formInput}
                  maxLength={30}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="lname">שם משפחה:</label>
                <input
                  type="text"
                  id="lname"
                  name="lname"
                  value={profileData.lname}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={styles.formInput}
                  maxLength={30}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="id">תעודת זהות:</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={profileData.id}
                  onChange={handleChange}
                  disabled={true} // תעודת זהות לא ניתנת לעריכה
                  className={styles.formInput}
                  maxLength={9}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email">אימייל:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={styles.formInput}
                />
              </div>
            </div>
            
            <div className={styles.buttonContainer}>
              {isEditing && (
                <>
                  <button type="submit" className={styles.saveButton}>
                    שמור
                  </button>
                  <button 
                    type="button" 
                    className={styles.cancelButton}
                    onClick={handleCancel}
                  >
                    ביטול
                  </button>
                </>
              )}
            </div>
          </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
